import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { getLinkToFindPage } from 'helpers/HelperFunctions';
import { getSkillLevel } from 'helpers/portfolioLayout';
import { getSkillIcon } from 'helpers/skillIcons';
import styles from './Skills.module.scss';

const RADIUS = 30;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const renderIcon = (skill) => {
  const icon = getSkillIcon(skill);

  if (icon?.logo) {
    return <img src={icon.logo} alt="" className={styles.logo} />;
  }

  if (icon?.Glyph) {
    const { Glyph } = icon;

    return (
      <span className={styles.glyph} aria-hidden="true">
        <Glyph size={24} strokeWidth={1.75} />
      </span>
    );
  }

  return (
    <span className={styles.initials} aria-hidden="true">
      {skill.name.slice(0, 2)}
    </span>
  );
};

const SkillRing = ({ skill, userType, className }) => {
  const level = getSkillLevel(skill.experience);
  const filled = level ? (CIRCUMFERENCE * skill.experience) / 100 : 0;
  const { linkToFindPage, url } = getLinkToFindPage(skill, userType);
  const NameTag = linkToFindPage ? 'a' : 'span';

  const ring = (
    <span
      className={styles.ring}
      role="img"
      aria-label={level ? `${level.label} in ${skill.name}` : skill.name}
    >
      <svg
        aria-hidden="true"
        width="64"
        height="64"
        viewBox="0 0 64 64"
        className={styles.ringSvg}
      >
        <circle
          cx="32"
          cy="32"
          r={RADIUS}
          fill="none"
          stroke="#E5E9EE"
          strokeWidth="3"
        />
        {level && (
          <circle
            cx="32"
            cy="32"
            r={RADIUS}
            fill="none"
            stroke={level.ring}
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={`${filled.toFixed(1)} ${CIRCUMFERENCE.toFixed(1)}`}
          />
        )}
      </svg>
      {renderIcon(skill)}
    </span>
  );

  return (
    <li className={`${styles.skill} ${className || ''}`}>
      {/* The level shows on hover. Touch screens hide the tooltip, and the
          ring's label carries the level for screen readers. */}
      {level ? (
        <OverlayTrigger
          placement="top"
          trigger={['hover']}
          overlay={
            <Tooltip
              id={`skill-level-${skill.id || skill.name}`}
              className={styles.levelTooltip}
            >
              {level.label} in {skill.name}
            </Tooltip>
          }
        >
          {ring}
        </OverlayTrigger>
      ) : (
        ring
      )}
      <NameTag
        className={styles.name}
        {...(linkToFindPage ? { href: url } : {})}
      >
        {skill.name}
      </NameTag>
    </li>
  );
};

SkillRing.propTypes = {
  skill: PropTypes.object.isRequired,
  userType: PropTypes.string,
  className: PropTypes.string,
};

export default memo(SkillRing);
